import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('edoverify_token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Impersonation state
  const [impersonation, setImpersonation] = useState(null);
  const [originalToken, setOriginalToken] = useState(localStorage.getItem('edoverify_original_token'));

  // Setup axios interceptor
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`);
          setUser(response.data);
          
          // Check for impersonation info
          if (response.data.impersonation) {
            setImpersonation(response.data.impersonation);
          } else {
            setImpersonation(null);
          }
        } catch (err) {
          console.error('Auth check failed:', err);
          localStorage.removeItem('edoverify_token');
          localStorage.removeItem('edoverify_original_token');
          setToken(null);
          setUser(null);
          setImpersonation(null);
          setOriginalToken(null);
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  // Login with email and password
  const login = useCallback(async (email, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem('edoverify_token', access_token);
      setToken(access_token);
      setUser(userData);
      setImpersonation(null);
      
      return userData;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem('edoverify_token');
    localStorage.removeItem('edoverify_original_token');
    setToken(null);
    setUser(null);
    setImpersonation(null);
    setOriginalToken(null);
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Start impersonating a user
  const startImpersonation = useCallback(async (userId) => {
    try {
      const response = await axios.post(`${API_URL}/api/users/${userId}/impersonate`);
      
      if (response.data.success) {
        // Store the original token before switching
        localStorage.setItem('edoverify_original_token', token);
        setOriginalToken(token);
        
        // Switch to impersonation token
        const impersonationToken = response.data.impersonation_token;
        localStorage.setItem('edoverify_token', impersonationToken);
        setToken(impersonationToken);
        
        // Update user to the target user
        setUser({
          ...response.data.target_user,
          country_scope: [],
          is_active: true,
          requires_2fa: false
        });
        
        setImpersonation({
          is_impersonating: true,
          impersonator_id: null, // Will be filled by /auth/me
          impersonator_name: null,
          read_only: true,
          target_user: response.data.target_user
        });
        
        return response.data;
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to start impersonation';
      throw new Error(errorMessage);
    }
  }, [token]);

  // Stop impersonating and return to original user
  const stopImpersonation = useCallback(async () => {
    if (originalToken) {
      localStorage.setItem('edoverify_token', originalToken);
      localStorage.removeItem('edoverify_original_token');
      setToken(originalToken);
      setOriginalToken(null);
      setImpersonation(null);
      
      // Refresh user data
      try {
        axios.defaults.headers.common['Authorization'] = `Bearer ${originalToken}`;
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
      } catch (err) {
        console.error('Failed to refresh user after stopping impersonation:', err);
      }
    }
  }, [originalToken]);

  // Refresh user data from server
  const refreshUser = useCallback(async () => {
    if (token) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`);
        setUser(response.data);
        if (response.data.impersonation) {
          setImpersonation(response.data.impersonation);
        }
        return response.data;
      } catch (err) {
        console.error('Failed to refresh user:', err);
        throw err;
      }
    }
  }, [token]);

  const hasPermission = useCallback((requiredRoles) => {
    if (!user) return false;
    if (!Array.isArray(requiredRoles)) requiredRoles = [requiredRoles];
    return requiredRoles.includes(user.role);
  }, [user]);

  const value = {
    user,
    token,
    loading,
    error,
    login,
    logout,
    refreshUser,
    hasPermission,
    isAuthenticated: !!user,
    // Impersonation
    impersonation,
    isImpersonating: !!impersonation?.is_impersonating,
    startImpersonation,
    stopImpersonation
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
